import { useEffect, useState, useRef } from "react";
import { classNames } from "primereact/utils";
import { REIDropDown, REIInputText, REIMultiSelect } from "../../../../components/ui";

interface StrategyComponentProps {
  selectedDropdownValue: any;
  handleDropdownChange: (e: any, dropdownName: string) => void;
  optionsdata: any;
  strategyKeywords: any;
  strategyOnChange: (e: any) => void;
  keywords: string[];
  resetAllFilters?: boolean;
}

const StrategyComponent: React.FC<StrategyComponentProps> = ({
  selectedDropdownValue,
  handleDropdownChange,
  optionsdata,
  strategyKeywords,
  strategyOnChange,
  keywords,
  resetAllFilters = false,
}) => {
  const [showStrategyOptions, setShowStrategyOptions] = useState(false);
  const [keywordPhrases, setKeywordPhrases] = useState("");
  const [excKeywordPhrases, setExcKeywordPhrases] = useState("");
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [valueChange, setValueChange] = useState(false);
  const processedRef = useRef(false);

  useEffect(() => {
    setValueChange(!valueChange);
  }, [optionsdata?.StrategiesCollection?.StrategiesAndKeywords]);

  useEffect(() => {
    setValueChange(!valueChange);
  }, [strategyKeywords]);

  useEffect(() => {
    if (resetAllFilters) {
      setKeywordPhrases("");
      setExcKeywordPhrases("");
      selectedDropdownValue.keywordStrategies =
        optionsdata?.StrategiesCollection?.StrategiesAndKeywords[1]?.FilterStrategyID || "";
      selectedDropdownValue.keywordStrategies =
        optionsdata?.StrategiesCollection?.StrategiesAndKeywords[0]?.FilterStrategyID || "";

      selectedDropdownValue.keywordPhrases = "";
      selectedDropdownValue.excKeywordPhrases = "";

      selectedDropdownValue.priceRangeMin = optionsdata?.PropertySpecifics?.PriceRange?.PriceRangeMin[0]?.ID || "";
      selectedDropdownValue.priceRangeMax = optionsdata?.PropertySpecifics?.PriceRange?.PriceRangeMax[0]?.ID || "";
    }
  }, [resetAllFilters]);

  useEffect(() => {
    if (keywords && keywords.length > 0) {
      const filteredKeywords = keywords.filter((item: any) => item.Label !== "Please Select...");
      setSelectedKeywords(filteredKeywords);
    }
  }, [keywords]);

  useEffect(() => {
    if (processedRef.current) return;

    const keywordStrategiesData = optionsdata?.StrategiesCollection?.StrategiesAndKeywords || [];
    const currentValue = selectedDropdownValue?.keywordStrategies || keywordStrategiesData[0]?.FilterStrategyID;

    if (currentValue && keywordStrategiesData.length > 0) {
      const selectedObject = keywordStrategiesData.find((item: any) => item.FilterStrategyID === currentValue);
      if (selectedObject) {
        processedRef.current = true;
        handleDropdownChange({ value: currentValue }, "keywordStrategies");
      }
    }
  }, [optionsdata?.StrategiesCollection?.StrategiesAndKeywords, handleDropdownChange]);

  const toggleStrategyOptions = () => {
    setShowStrategyOptions(!showStrategyOptions);
  };

  const renderDropdown = (label: string, filterName: string, data: any, optionValue: string, classNames?: string) => (
    <REIDropDown
      id={`strat-drop-${filterName.toLowerCase()}`}
      value={selectedDropdownValue?.[filterName] || data[0]?.[optionValue]}
      onSelectionChange={(e) => handleDropdownChange(e, filterName)}
      data={data}
      label="Label"
      optionValue={optionValue}
      className={classNames}
    />
  );

  return (
    <div className="">
      <div className="flex flex-col xl:flex-row lg:w-full gap-1">
        <div className="flex-0 w-full">
          <div className="flex flex-col lg:flex-row lg:w-full gap-1">
            <div className="flex-0 w-full">
              <label className="block text-sm font-medium">Price range</label>
              {renderDropdown(
                "Price Range",
                "priceRangeMin",
                optionsdata?.PropertySpecifics?.PriceRange?.PriceRangeMin || [],
                "ListingPrice_From_ID"
              )}
            </div>
            <div className="flex-0 lg:pt-7">
              <span>to</span>
            </div>
            <div className="flex-0 w-full">
              {renderDropdown(
                "Price Range",
                "priceRangeMax",
                optionsdata?.PropertySpecifics?.PriceRange?.PriceRangeMax || [],
                "ListingPrice_To_ID",
                "w-full lg:mt-5"
              )}
            </div>
          </div>
        </div>
        <div className="flex-0 w-full">
          <label className="block text-sm font-medium">Keyword strategies</label>
          {renderDropdown(
            "Keyword strategies",
            "keywordStrategies",
            optionsdata?.StrategiesCollection?.StrategiesAndKeywords || [],
            "FilterStrategyID"
          )}
        </div>
      </div>

      {showStrategyOptions && (
        <>
          <div className="w-full">
            <label className="block text-sm font-medium">Select keywords</label>
            {selectedKeywords && selectedKeywords.length > 0 && (
              <REIMultiSelect
                id="strat-mult-keywords"
                value={strategyKeywords}
                onChange={strategyOnChange}
                options={selectedKeywords}
                optionLabel="Label"
                placeholder="Select Keywords"
                maxSelectedLabels={3}
                className="w-full md:w-20rem"
              />
            )}
          </div>
          <div className="flex flex-col lg:flex-row lg:w-full gap-1">
            <div className="flex-0 w-full">
              <label className="block text-sm font-medium">Add these keywords (space-separated)</label>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row lg:w-full gap-1">
            <div className="flex-0 lg:w-[80%] w-full">
              <REIInputText
                id="strat-txt-keywords"
                type="text"
                name="keywords"
                className="w-full"
                onChange={(e) => {
                  setKeywordPhrases(e.target.value);
                  handleDropdownChange({ value: e.target.value }, "keywordPhrases");
                }}
                value={selectedDropdownValue?.keywordPhrases || keywordPhrases}
              />
            </div>
            <div className="flex-0 lg:w-[80%] w-full">
              {renderDropdown(
                "Keyword Phrase",
                "keywordPhrase",
                optionsdata?.Strategy?.KeywordPhrase || [],
                "FilterKeywordSearchTypeID",
                "w-full"
              )}
            </div>
          </div>
          <div className="flex flex-col lg:flex-row lg:w-full gap-1">
            <div className="flex-0 w-full">
              <label className="block text-sm font-medium">Exclude these keywords (space-separated)</label>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row lg:w-full gap-1">
            <div className="flex-0 lg:w-[80%] w-full">
              <REIInputText
                id="strat-txt-exckeywords"
                type="text"
                name="keywords"
                className="w-full"
                onChange={(e) => {
                  setExcKeywordPhrases(e.target.value);
                  handleDropdownChange({ value: e.target.value }, "excKeywordPhrases");
                }}
                value={selectedDropdownValue?.excKeywordPhrases || excKeywordPhrases}
              />
            </div>
            <div className="flex-0 lg:w-[80%] w-full">
              {renderDropdown(
                "Exclude Keyword Phrase",
                "excKeywordPhrase",
                optionsdata?.Strategy?.ExcKeywordPhrase || [],
                "FilterExcludeKeywordSearchTypeID",
                "w-full"
              )}
            </div>
          </div>
        </>
      )}

      <div className="col-span-12 text-end mt-2 text-blue-700">
        <span
          id="strat-lnk-morestrategy"
          data-testid="strat-lnk-morestrategy"
          className="mr-1 pt-2 cursor-pointer"
          onClick={toggleStrategyOptions}
        >
          {showStrategyOptions ? "Fewer" : "More"} strategy options
        </span>
        <i
          id="strat-icon-morestrategy"
          data-testid="strat-icon-morestrategy"
          className={classNames("pi cursor-pointer", !showStrategyOptions ? "pi-angle-down" : "pi-angle-up")}
          onClick={toggleStrategyOptions}
        ></i>
      </div>
    </div>
  );
};

export default StrategyComponent;
